import { Component, computed, inject, input, output } from '@angular/core';
import { AuthService } from '@lib/auth/auth.service';
import { ConsultantModel } from '@lib/models/consultant/consultant.model';
import { DemandeHabilitation } from '@lib/models/demande-habilitation/demande-habilitation.model';
import { ShmHabilitationType } from '@lib/services/consultant/consultant.service';
import { OuiDialogService } from 'omnium-ui/dialog';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { firstValueFrom } from 'rxjs';
import {
  HabilitationInitiationDialogComponent,
  HabilitationInitiationDialogData,
} from '../../dialogs/habilitation-initiation-dialog/habilitation-initiation-dialog.component';
import { HabilitationComponent } from './habilitation/habilitation.component';

const TERMINAL_STATUSES = new Set(['valide', 'annule', 'expire', 'refuse']);

@Component({
  selector: 'lib-fiche-consultant-habilitations',
  imports: [HabilitationComponent],
  templateUrl: './fiche-consultant-habilitations.component.html',
  styleUrl: './fiche-consultant-habilitations.component.scss',
})
export class FicheConsultantHabilitationsComponent {
  private readonly authService = inject(AuthService);
  private readonly dialogService = inject(OuiDialogService);
  private readonly snackbarService = inject(OuiSnackbarService);

  readonly consultant = input.required<ConsultantModel>();
  readonly isBo = input(false);
  readonly refreshRequested = output<void>();

  readonly currentDemandes = computed(() => {
    const demandes = this.consultant().demandeHabilitations ?? [];
    const current = (type: string): DemandeHabilitation | undefined => {
      const matching = demandes.filter(demande => demande.type === type);
      return (
        matching.find(demande => !TERMINAL_STATUSES.has(demande.statut)) ?? matching[0]
      );
    };
    return {
      immobilier: current('immobilier'),
      assurance: current('assurance'),
      financement: current('financement'),
      cif: current('cif'),
      alpsi: current('alpsi'),
    };
  });

  readonly canInitiate = computed(() => {
    const consultant = this.consultant();
    const isShm = this.isBo() && !this.authService.isOnlyGcom();
    const hasOpenRequest = (type: string): boolean =>
      consultant.demandeHabilitations?.some(
        demande => demande.type === type && !TERMINAL_STATUSES.has(demande.statut)
      ) ?? false;
    return {
      immobilier:
        isShm &&
        !hasOpenRequest('immobilier') &&
        !consultant.habilitations?.immobilier?.actif,
      assurance:
        isShm &&
        !hasOpenRequest('assurance') &&
        !consultant.habilitations?.assurance?.actif,
      financement:
        isShm &&
        !hasOpenRequest('financement') &&
        !consultant.habilitations?.iob?.actif &&
        !consultant.habilitations?.financement?.actif,
    };
  });

  async openInitiation(type: ShmHabilitationType): Promise<void> {
    const completed = await firstValueFrom(
      this.dialogService
        .openDialog(HabilitationInitiationDialogComponent, {
          codeConsultant: this.consultant().code,
          type,
        } satisfies HabilitationInitiationDialogData)
        .afterClosed()
    );
    if (completed !== true) return;
    this.snackbarService.open('Parcours initié avec succès', 'success');
    this.refreshRequested.emit();
  }
}
